import { ReactNode } from "react"
import clsx from "clsx"

export type SortOrder = "asc" | "desc"

export interface Column<T> {
  header: string
  align?: "right"
  sortable?: boolean
  sortKey?: string
  width?: string
  /**
   * Hides the column below this breakpoint. A nine-column row on a phone means
   * dragging sideways for one value; opening the row still shows everything.
   */
  hideBelow?: "sm" | "md" | "lg" | "xl"
  /** Lets long text wrap instead of widening the table. */
  wrap?: boolean
  render: (row: T, index: number) => ReactNode
}

/** Tailwind needs whole class names, so the breakpoints are spelled out. */
const HIDE_BELOW: Record<NonNullable<Column<unknown>["hideBelow"]>, string> = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell"
}

interface DataTableProps<T> {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  sortKey?: string
  sortOrder?: SortOrder
  onSort?: (key: string) => void
}

export function DataTable<T>({ rows, columns, rowKey, onRowClick, sortKey, sortOrder, onSort }: DataTableProps<T>) {
  const fixed = columns.some((col) => col.width)
  return (
    <div className="overflow-x-auto">
      <table className={clsx("table table-compact w-full", fixed && "table-fixed")}>
        <thead>
          <tr className="bg-gray-50 dark:bg-[rgba(255,255,255,0.04)]">
            {columns.map((col) => {
              const key = col.sortKey ?? col.header
              const isActive = Boolean(col.sortable) && sortKey === key
              return (
                <th
                  key={col.header}
                  style={col.width ? { width: col.width } : undefined}
                  className={clsx(
                    "px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400",
                    col.align === "right" ? "text-right" : "text-left",
                    col.hideBelow && HIDE_BELOW[col.hideBelow]
                  )}
                >
                  {col.sortable && onSort ? (
                    <button
                      type="button"
                      onClick={() => onSort(key)}
                      className={clsx(
                        "inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-gray-700 dark:hover:text-gray-200",
                        col.align === "right" && "flex-row-reverse",
                        isActive && "text-gray-700 dark:text-gray-200"
                      )}
                    >
                      <span>{col.header}</span>
                      <span className="text-[10px] leading-none">{isActive ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}</span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row)}
              className={clsx(
                "border-b border-gray-100 dark:border-[rgba(255,255,255,0.05)] hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.03)]",
                onRowClick && "cursor-pointer"
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.header}
                  className={clsx(
                    "px-4 py-2 text-sm text-gray-700 dark:text-gray-200",
                    col.wrap ? "whitespace-normal" : "whitespace-nowrap",
                    col.align === "right" && "text-right",
                    col.hideBelow && HIDE_BELOW[col.hideBelow]
                  )}
                >
                  {col.render(row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
